import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, BarChart3, PieChart, LineChart, RefreshCw, Download, Maximize2 } from "lucide-react";
interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
  trend?: 'up' | 'down' | 'stable';
  percentage?: number;
}
interface InteractiveChartProps {
  title: string;
  description?: string;
  data: ChartDataPoint[];
  type?: 'bar' | 'pie' | 'line' | 'donut';
  height?: number;
  animated?: boolean;
  realTimeUpdate?: boolean;
  updateInterval?: number;
  onDataUpdate?: () => Promise<ChartDataPoint[]>;

  // Interactive features
  allowFilter?: boolean;
  allowExport?: boolean;
  allowFullscreen?: boolean;
  className?: string;
}
export const InteractiveChart: React.FC<InteractiveChartProps> = ({
  title,
  description,
  data: initialData,
  type = 'bar',
  height = 300,
  animated = true,
  realTimeUpdate = false,
  updateInterval = 30000,
  onDataUpdate,
  // Interactive features
  allowFilter = true,
  allowExport = false,
  allowFullscreen = false,
  className
}) => {
  const [data, setData] = useState<ChartDataPoint[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [isUpdating, setIsUpdating] = useState(false);

  // التحديث التلقائي
  useEffect(() => {
    if (!realTimeUpdate || !onDataUpdate) return;
    const interval = setInterval(async () => {
      try {
        setIsUpdating(true);
        const newData = await onDataUpdate();
        setData(newData);
      } catch (error) {
        console.error('Chart update failed:', error);
      } finally {
        setIsUpdating(false);
      }
    }, updateInterval);
    return () => clearInterval(interval);
  }, [realTimeUpdate, onDataUpdate, updateInterval]);

  // حساب الإحصائيات
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const maxValue = Math.max(...data.map(item => item.value));
  const trend = data.length > 1 && data[data.length - 1].value > data[0].value;
  const handleRefresh = async () => {
    if (!onDataUpdate) return;
    setLoading(true);
    try {
      const newData = await onDataUpdate();
      setData(newData);
    } catch (error) {
      console.error('Manual refresh failed:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8," + "Label,Value,Percentage\n" + data.map(item => `${item.label},${item.value},${item.percentage || 0}%`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${title.replace(/\s+/g, '_')}_chart_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const renderBarChart = () => <div className="flex items-end justify-between h-full gap-2 px-4 pb-4">
      {data.map((item, index) => <div key={index} className="flex flex-col items-center flex-1 group">
          <div className="flex-1 flex items-end w-full">
            <div className={cn("w-full rounded-t-md transition-all duration-500 group-hover:opacity-80", animated && "animate-scale-in")} style={{
          height: `${item.value / maxValue * 100}%`,
          backgroundColor: item.color || '#3b82f6',
          animationDelay: `${index * 100}ms`
        }} />
          </div>
          
          <div className="mt-2 text-center">
            <div className="text-xs font-medium text-card-foreground truncate w-full">
              {item.label}
            </div>
            <div className="text-xs text-muted-foreground">
              {item.value}
            </div>
          </div>
        </div>)}
    </div>;
  const renderPieChart = () => {
    const centerX = 120;
    const centerY = 120;
    const radius = 80;
    let cumulativePercentage = 0;
    return <div className="flex items-center justify-center">
        <div className="relative">
          <svg width="240" height="240" className="transform -rotate-90">
            {data.map((item, index) => {
            const percentage = item.value / totalValue * 100;
            const strokeDasharray = `${percentage * 2.51} ${251.2 - percentage * 2.51}`;
            const strokeDashoffset = -cumulativePercentage * 2.51;
            cumulativePercentage += percentage;
            return <circle key={index} cx={centerX} cy={centerY} r="40" fill="transparent" stroke={item.color || `hsl(${index * 45}, 70%, 50%)`} strokeWidth="20" strokeDasharray={strokeDasharray} strokeDashoffset={strokeDashoffset} className={cn("transition-all duration-500 hover:stroke-[24]", animated && "animate-scale-in")} style={{
              animationDelay: `${index * 200}ms`
            }} />;
          })}
          </svg>
          
          {/* المركز */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-card-foreground">{totalValue}</div>
              <div className="text-xs text-muted-foreground">المجموع</div>
            </div>
          </div>
        </div>
        
        {/* المفاتيح */}
        <div className="ml-6 space-y-2">
          {data.map((item, index) => <div key={index} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{
            backgroundColor: item.color || `hsl(${index * 45}, 70%, 50%)`
          }} />
              <span className="text-sm text-card-foreground">{item.label}</span>
              <span className="text-xs text-muted-foreground">
                ({Math.round(item.value / totalValue * 100)}%)
              </span>
            </div>)}
        </div>
      </div>;
  };
  const renderChart = () => {
    switch (type) {
      case 'bar':
        return renderBarChart();
      case 'pie':
      case 'donut':
        return renderPieChart();
      case 'line':
        return renderBarChart();
      // مبسط للمثال
      default:
        return renderBarChart();
    }
  };
  return <Card className={cn("relative overflow-hidden", isUpdating && "animate-pulse", className)}>
      
      
      
      
      {/* مؤشر التحديث */}
      {isUpdating && <div className="absolute top-0 left-0 right-0 h-1">
          <div className="h-full bg-primary animate-pulse" />
        </div>}
    </Card>;
};

// مخططات متخصصة
export const ClientStatusChart: React.FC<{
  data: any[];
}> = ({
  data
}) => {
  const chartData = data.map(item => ({
    label: item.status_name,
    value: item.count,
    color: item.color,
    percentage: Math.round(item.count / data.reduce((sum, d) => sum + d.count, 0) * 100)
  }));
  return <InteractiveChart title="توزيع حالات العملاء" description="إحصائيات العملاء حسب الحالة" data={chartData} type="pie" realTimeUpdate allowExport allowFilter />;
};